import React, { useState, useEffect, useRef } from 'react';
import { isMobileDevice } from 'shared/utils';
import TextToDownload from '../textToDownload';
import AppleStoreIcon from 'images/apple-store-icon.png';
import GooglePlayIcon from 'images/google-play-icon.png';
import scrollIntoView from 'scroll-into-view';
import { H3, Body } from 'scy/text';
import { sansSerifMedium } from 'scy/text/styles.mod.scss';
import { textCenter } from 'scy/utils.mod.scss';
import { inlineBlock } from 'scy/display.mod.scss';
import { BUTTON_CLASSES } from 'scy/buttons';
import { TextHR } from 'scy/HR';
import margin from 'scy/margin.mod.scss';
import padding from 'scy/padding.mod.scss';
import { appButton } from 'shared/components/footer/styles.mod.scss';

export default function PostSignup({ message, promoCodes, shopNow }) {
  const container = useRef();

  useEffect(() => {
    if (container.current) {
      const rect = container.current.getBoundingClientRect();
      if (rect && rect.top < 0) scrollIntoView(container.current);
    }
  }, []);

  return (
    <div
      ref={container}
      className={`${padding.left_sm} ${padding.right_sm} ${textCenter}`}
    >
      {message === 'welcome' ? (
        <Welcome shopNow={shopNow} />
      ) : (
        <>
          <Title>
            {message === 'not-new-user'
              ? 'Welcome Back'
              : 'Welcome to the Good Life'}
          </Title>
          {message === 'not-new-user' && <NotNewUser />}
          {message === 'promo-invalid' && <PromoInvalid />}
          {message === 'promo-success' && (
            <PromoSuccess promoCodes={promoCodes} />
          )}

          <CTA shopNow={shopNow} />
        </>
      )}
    </div>
  );
}

function Title({ children }) {
  return (
    <H3
      tag="h1"
      marginClass={`${margin.top_0} ${margin.bottom_md} ${
        margin.tablet_bottom_lg
      }`}
    >
      {children}
    </H3>
  );
}

function Content({ children }) {
  return (
    <Body
      marginClass={`${margin.top_0} ${margin.bottom_md} ${
        margin.tablet_bottom_lg
      } ${padding.tablet_left_md} ${padding.tablet_right_md}`}
    >
      {children}
    </Body>
  );
}

function CTA({ shopNow }) {
  return (
    <div className={`${margin.bottom_md} ${margin.tablet_bottom_lg}`}>
      <a
        href="/"
        onClick={e => {
          if (shopNow) {
            e.preventDefault();
            shopNow();
          }
        }}
        className={`${BUTTON_CLASSES.primary} ${inlineBlock}`}
      >
        Shop now
      </a>
    </div>
  );
}

function Welcome({ shopNow }) {
  const [canDownloadApp, setCanDownloadApp] = useState(false);

  useEffect(() => {
    setCanDownloadApp(isMobileDevice());
  }, []);

  return (
    <>
      <Title>Welcome to the Good Life</Title>

      <Content>
        Enjoy $5 off your first purchase with the code:{' '}
        <span className={sansSerifMedium}>GOODLIFE</span>
      </Content>

      <CTA shopNow={shopNow} />

      {canDownloadApp ? (
        <div className={padding.bottom_lg}>
          <TextHR>Or Download the App</TextHR>
          <div>
            <a href="https://app.adjust.com/vdebgr">
              <img src={AppleStoreIcon} className={appButton} />
            </a>
            <a href="https://app.adjust.com/qxnd5g">
              <img src={GooglePlayIcon} className={appButton} />
            </a>
          </div>
        </div>
      ) : (
        <div>
          <TextHR>Or Get the App</TextHR>
          <div className={padding.md}>
            <TextToDownload section="Post-Signup Page" wide />
          </div>
        </div>
      )}
    </>
  );
}

function NotNewUser() {
  return (
    <Content>
      The promo code you entered is not valid for returning customers. If you
      have another promo code, you can still enter it at checkout 😉{' '}
    </Content>
  );
}

function PromoInvalid() {
  return (
    <Content>
      The promo code you entered was not valid. If you have another promo code,
      you can still enter it at checkout 😉{' '}
    </Content>
  );
}

function PromoSuccess({ promoCodes = [] }) {
  const promoCodeVal =
    promoCodes.length && !!promoCodes[0] ? promoCodes[0].val : null;

  return (
    <Content>
      A {promoCodeVal ? `$${promoCodeVal}` : ''} credit has been added to your
      account. The discount will be applied to your cart at checkout. Cheers 😉{' '}
    </Content>
  );
}
